import { Injectable } from "@angular/core";

/**
 * sessionStorage key for a discount code applied manually on the payment
 * page (via the "Apply" button, or re-validated from a prior stored code).
 * Deliberately session-scoped — the same lifetime as the offer-link coupon
 * `PendingCouponService` uses (issue #592 decision 2) — per the human
 * product decision on issue #594: a manually applied code survives a
 * reload and in-app navigation within the same tab, and is gone once the
 * tab closes. It is a **plain, unprefixed** sessionStorage key, independent
 * of `SharedDataService`'s `vengreso.*` localStorage-backed store the old
 * (pre-#594) persistence used, so a masquerade session switching between
 * `localStorage`/`sessionStorage` can never resurrect it either.
 *
 * Only the CODE is ever persisted here — never a dollar amount. The amount
 * a stored code is worth is always recomputed by re-validating it against
 * the plan currently on screen (issue #594 decision 1): a discount amount
 * computed for one plan must never be shown or sent for another.
 */
const STORED_DISCOUNT_STORAGE_KEY = "storedDiscountCode";

@Injectable({ providedIn: "root" })
export class StoredDiscountService {
  /** Reads the stashed code without clearing it. */
  peek(): string | null {
    try {
      return sessionStorage.getItem(STORED_DISCOUNT_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** Persists a validated code so a later reload / in-app navigation can restore and re-validate it. */
  set(code: string): void {
    try {
      sessionStorage.setItem(STORED_DISCOUNT_STORAGE_KEY, code);
    } catch {
      // sessionStorage unavailable (private mode, quota) — nothing more to do.
    }
  }

  /** Drops the stashed code, if any. */
  clear(): void {
    try {
      sessionStorage.removeItem(STORED_DISCOUNT_STORAGE_KEY);
    } catch {
      // sessionStorage unavailable (private mode, quota) — nothing to clear.
    }
  }

  /**
   * Clears the stashed code once the user has navigated outside the
   * pricing/checkout funnel, mirroring `PendingCouponService.clearIfOutsideFunnel`
   * so the two share one policy for what counts as "still in the funnel".
   */
  clearIfOutsideFunnel(url: string): void {
    if (!/^\/(pricing|plans|new-plan|payment-details|session\/verify-email)(\/|\?|$)/.test(url)) {
      this.clear();
    }
  }
}
